// ONE mapping dialog for every PSP. The UI is identical for all providers; the ONLY thing that varies is
// the `api` object the caller passes, which points at that PSP's EXISTING endpoints (no data-source change,
// no new backend). The PSP is a parameter, not a different screen. `api` = { suggest?, devoteeSearch,
// productSearch, customerPledges, saveMapping } — each a function returning a promise.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Grid, Chip,
  CircularProgress, Alert, Button, Divider, Autocomplete, TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { fmtMoney, fmtDate, ACCOUNT_LABEL } from './reconShared';

const confColor = (c) => (c === 'high' ? 'success' : c === 'medium' ? 'warning' : 'default');

// Normalize whatever the PaySimple / AuthNet subscription row looks like into the one shape the dialog
// renders. Missing fields render as "—"; the layout is identical for every provider.
const GENERIC_NAME = /^(recurring ?payment|recurring)$/i; // PSP schedule-type labels — not a real name
const validDate = (d) => { const t = new Date(d); return (d && !Number.isNaN(t.getTime()) && t.getFullYear() >= 2000) ? d : null; };
function normalizeSub(sub, provider, account) {
  if (!sub) return null;
  const total = sub.total_occurrences == null ? null : Number(sub.total_occurrences);
  const nm = (sub.name && !GENERIC_NAME.test(String(sub.name).trim())) ? sub.name : `Subscription ${sub.id}`;
  return {
    provider,
    account: account || sub.account || null,
    id: String(sub.id),
    name: nm,
    donor_name: sub.donor_name || null,
    amount: Number(sub.amount) || 0,
    past_occurrences: sub.past_occurrences != null ? Number(sub.past_occurrences) : (sub.stats?.paid_count ?? null),
    total_occurrences: total,
    next_payment_date: validDate(sub.next_payment_date),
    mapping: sub.mapping || null,
  };
}

export default function SubscriptionMappingDialog({ open, sub: rawSub, provider, account, api, onClose, onSaved }) {
  const sub = normalizeSub(rawSub, provider, account);
  const [suggest, setSuggest] = useState(null);
  const [loadingSug, setLoadingSug] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pledges, setPledges] = useState([]);
  const [prodOpts, setProdOpts] = useState([]);
  const [devOpts, setDevOpts] = useState([]);
  const [splits, setSplits] = useState([{ target: null, amount: '' }]);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const charge = Number(sub?.amount || 0);

  const loadPledges = useCallback(async (customerId) => {
    try { const r = await api.customerPledges(customerId); setPledges(Array.isArray(r?.pledges) ? r.pledges : []); }
    catch { setPledges([]); }
  }, [api]);

  useEffect(() => {
    if (!rawSub) return undefined;
    setSaveErr(''); setProdOpts([]); setSuggest(null);
    const m = rawSub.mapping || null;
    if (m) {
      setSelected({ customer_id: m.customer_id, name: m.customer_name || `#${m.customer_id}`, email: m.customer_email });
      setSplits((Array.isArray(m.splits) && m.splits.length ? m.splits : [{}]).map((s) => ({
        target: s.product_id ? { product_id: s.product_id, variation_id: s.variation_id || 0, label: s.label } : null,
        amount: s.amount != null ? String(s.amount) : '',
      })));
      loadPledges(m.customer_id);
    } else { setSelected(null); setPledges([]); setSplits([{ target: null, amount: '' }]); }
    let cancel = false;
    (async () => {
      if (!api.suggest) { setSuggest({ candidates: [] }); return; }
      setLoadingSug(true);
      try { const r = await api.suggest(rawSub); if (!cancel) setSuggest(r || { candidates: [] }); }
      catch { if (!cancel) setSuggest({ candidates: [] }); }
      finally { if (!cancel) setLoadingSug(false); }
    })();
    return () => { cancel = true; };
  }, [rawSub, loadPledges]);

  const pickDevotee = (c) => { setSelected({ customer_id: c.customer_id, name: c.name, email: c.email }); loadPledges(c.customer_id); setSplits([{ target: null, amount: '' }]); };
  const setSplit = (i, k, v) => setSplits((p) => p.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const searchProducts = async (term) => {
    if (!term || term.length < 2) { setProdOpts([]); return; }
    try { const r = await api.productSearch(term); setProdOpts(Array.isArray(r?.products) ? r.products : []); } catch { setProdOpts([]); }
  };
  const searchDevotees = async (term) => {
    if (!term || term.length < 2) { setDevOpts([]); return; }
    try { const r = await api.devoteeSearch(term); setDevOpts(Array.isArray(r?.devotees) ? r.devotees : []); } catch { setDevOpts([]); }
  };

  const productsSum = splits.reduce((a, s) => a + (parseFloat(s.amount) || 0), 0);
  const fee = Math.round((charge - productsSum) * 100) / 100;
  const allValid = selected && splits.every((s) => s.target && (parseFloat(s.amount) || 0) > 0) && productsSum > 0 && productsSum <= charge + 0.005;

  const doSave = async () => {
    setSaving(true); setSaveErr('');
    try {
      const cleanSplits = splits.filter((s) => s.target && (parseFloat(s.amount) || 0) > 0).map((s) => ({
        product_id: s.target.product_id, variation_id: s.target.variation_id || 0, label: s.target.label, amount: parseFloat(s.amount),
      }));
      await api.saveMapping({ sub: rawSub, customerId: selected.customer_id, splits: cleanSplits });
      onSaved?.(rawSub);
    } catch (e) { setSaveErr(e?.response?.data?.error || e?.message || 'Save failed'); setSaving(false); }
  };

  // Only OPEN pledges (balance remaining) are offered in the dropdown; completed/fully-paid pledges
  // (balance_total <= 0) are hidden. An existing mapping's saved target still renders via `value`, so
  // editing a mapping that already points at a now-completed pledge is unaffected.
  const openPledges = pledges.filter((p) => !(p.balance_total != null && Number(p.balance_total) <= 0.005));
  const seen = new Set(openPledges.map((p) => `${p.product_id}:${p.variation_id}`));
  const options = [...openPledges, ...prodOpts.filter((p) => !seen.has(`${p.product_id}:${p.variation_id}`))];
  const installments = sub?.total_occurrences == null
    ? (sub?.past_occurrences != null ? `${sub.past_occurrences} of ∞` : '∞ ongoing')
    : `${sub?.past_occurrences ?? 0} of ${sub.total_occurrences}`;

  return (
    <Dialog open={!!open && !!rawSub} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>Map subscription → devotee &amp; allocation</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{sub?.name}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1.5} sx={{ mb: 1 }}>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Subscription</Typography><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{sub?.id}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Account</Typography><Typography variant="body2">{sub?.account ? (ACCOUNT_LABEL[sub.account] || sub.account) : '—'}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Charge</Typography><Typography variant="body2">{fmtMoney(sub?.amount)} / month</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Customer</Typography><Typography variant="body2">{sub?.donor_name || '—'}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Installments</Typography><Typography variant="body2">{installments}</Typography></Grid>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Next payment</Typography><Typography variant="body2">{sub?.next_payment_date ? fmtDate(sub.next_payment_date) : '—'}</Typography></Grid>
        </Grid>
        <Divider sx={{ my: 1.5 }} />

        <Typography variant="overline" sx={{ color: 'text.secondary' }}>Devotee</Typography>
        {selected ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 1, borderRadius: 1, border: 1, borderColor: 'success.main', bgcolor: 'action.hover' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>{selected.name}{selected.email ? ` · ${selected.email}` : ''}</Typography>
            <Chip size="small" color="success" label="selected" />
            <Button size="small" onClick={() => { setSelected(null); setPledges([]); }}>change</Button>
          </Box>
        ) : (
          <>
            {loadingSug && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><CircularProgress size={16} /><Typography variant="caption" color="text.secondary">Finding matching devotees…</Typography></Box>}
            {Array.isArray(suggest?.candidates) && suggest.candidates.map((c) => (
              <Box key={c.customer_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, p: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.name}</Typography>
                  {c.email && <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>{c.email}</Typography>}
                </Box>
                {c.reason && <Chip size="small" color={confColor(c.confidence)} variant={c.confidence === 'low' ? 'outlined' : 'filled'} label={c.reason} />}
                <Button size="small" variant="contained" onClick={() => pickDevotee(c)}>Select</Button>
              </Box>
            ))}
            <Autocomplete
              size="small" sx={{ mt: 1.5 }} options={devOpts} filterOptions={(x) => x}
              getOptionLabel={(o) => (o ? `${o.name}${o.email ? ` · ${o.email}` : ''}` : '')}
              isOptionEqualToValue={(o, v) => o.customer_id === v.customer_id}
              onChange={(e, v) => { if (v) pickDevotee(v); }}
              onInputChange={(e, v, reason) => { if (reason === 'input') searchDevotees(v); }}
              noOptionsText="Type a name or email…"
              renderInput={(params) => <TextField {...params} placeholder="Search devotee by name / email…" />}
            />
          </>
        )}

        {selected && (
          <>
            <Typography variant="overline" sx={{ color: 'text.secondary', mt: 1.5, display: 'block' }}>Allocation — amount toward each pledge / product</Typography>
            {splits.map((s, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                <Autocomplete
                  size="small" sx={{ flex: 1 }} options={options} value={s.target}
                  isOptionEqualToValue={(o, v) => o.product_id === v.product_id && (o.variation_id || 0) === (v.variation_id || 0)}
                  getOptionLabel={(o) => o?.label || ''}
                  onChange={(e, v) => setSplit(i, 'target', v)}
                  onInputChange={(e, v, reason) => { if (reason === 'input') searchProducts(v); }}
                  renderOption={(props, o) => (
                    <li {...props} key={`${o.product_id}:${o.variation_id || 0}`}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                        <span>{o.label}</span>
                        {o.balance_total != null && <Typography variant="caption" color="text.secondary">bal {fmtMoney(o.balance_total)}</Typography>}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} placeholder="Open pledge or search product…" />}
                />
                <TextField size="small" type="number" placeholder="0.00" value={s.amount} onChange={(e) => setSplit(i, 'amount', e.target.value)} sx={{ width: 110 }} />
                <IconButton size="small" onClick={() => setSplits((p) => p.filter((_, idx) => idx !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => setSplits((p) => [...p, { target: null, amount: '' }])}>Add line</Button>

            <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1, border: 1, borderStyle: 'dashed', borderColor: allValid ? 'success.main' : 'warning.main', bgcolor: 'action.hover' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">Products</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtMoney(productsSum)}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">Fee (plug line)</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: fee < -0.005 ? 'error.main' : 'text.secondary' }}>{fmtMoney(fee)}</Typography></Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" sx={{ fontWeight: 700 }}>Total (charge)</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtMoney(charge)}</Typography></Box>
            </Box>
            {productsSum > charge + 0.005 && <Alert severity="error" sx={{ mt: 1 }}>Products exceed the charge amount — reduce to {fmtMoney(charge)} or less.</Alert>}
          </>
        )}
        {saveErr && <Alert severity="error" sx={{ mt: 1.5 }}>{saveErr}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={doSave} disabled={!allValid || saving}>{saving ? 'Saving…' : 'Save mapping'}</Button>
      </DialogActions>
    </Dialog>
  );
}
